// status-recoveries-from-journal.ts
//
// Orchestrator recovery (3.40.0, D2.9): `status --json` `payload.recoveries[]`
// — one row per recovery trigger (`trigger_seq`) the controller acted on —
// and `payload.orchestrator_recovery_enabled` (mission snapshot: enabled ∧
// cap > 0; always emitted by this CLI, absent from ≤ 3.39). Lock-free journal
// read (status answers while `run` holds the writer lease — Bug H).
import { readJournalRecordsLockFree } from "./attempt-finished-journal-reader.js";
import {
  isOrchestratorRecoveryActive,
  readOrchestratorRecoveryConfig,
} from "./orchestrator-recovery-config.js";
import {
  RECOVERY_TICKET_OP_KIND,
  type RecoveryOpRecord,
  type RecoveryStatusResult,
} from "./recovery-guards.js";
import { recoveryLedgerFromRecords } from "./recovery-ledger-from-journal.js";
import type { RecoveryTicketTrigger } from "./recovery-ticket-shape.js";

export interface RecoveryStatusItem {
  source_ticket: string;
  trigger: RecoveryTicketTrigger;
  /** Applied ticket; for `pending`, the id the last try names. Absent when `skipped:*`. */
  recovery_ticket?: string;
  result: RecoveryStatusResult;
  /** Current status of `recovery_ticket` (journal). Absent when `skipped:*` or unknown. */
  final_status?: string;
}

/** One row per `trigger_seq`, in the order the first op was journaled. */
export function recoveryStatusItems(
  ledger: readonly RecoveryOpRecord[],
  ticketStatus: ReadonlyMap<string, string>,
): RecoveryStatusItem[] {
  const bySeq = new Map<number, RecoveryOpRecord[]>();
  for (const op of ledger) bySeq.set(op.trigger_seq, [...(bySeq.get(op.trigger_seq) ?? []), op]);
  return [...bySeq.values()].map((ops) => {
    const head = {
      source_ticket: ops[0]?.source_ticket ?? "",
      trigger: ops[0]?.trigger as RecoveryTicketTrigger,
    };
    const skip = ops.find((o) => o.result === "skipped");
    if (skip?.reason !== undefined) return { ...head, result: `skipped:${skip.reason}` as const };
    const tries = ops.filter((o) => o.operation_kind === RECOVERY_TICKET_OP_KIND);
    const applied = tries.find((o) => o.result === "applied");
    const ticket = applied?.ticket_id ?? tries.at(-1)?.ticket_id;
    const status = ticket === undefined ? undefined : ticketStatus.get(ticket);
    return {
      ...head,
      ...(ticket === undefined ? {} : { recovery_ticket: ticket }),
      result: applied === undefined ? "pending" : "applied",
      ...(status === undefined ? {} : { final_status: status }),
    };
  });
}

export interface StatusRecoveryFields {
  recoveries?: RecoveryStatusItem[];
  orchestrator_recovery_enabled: boolean;
}

/** Best-effort: unreadable journal ⇒ no rows; never throws. */
export async function readStatusRecoveryFields(controlRoot: string): Promise<StatusRecoveryFields> {
  const [config, records] = await Promise.all([
    readOrchestratorRecoveryConfig(controlRoot),
    readJournalRecordsLockFree(controlRoot),
  ]);
  const ticketStatus = new Map<string, string>();
  for (const { delta } of records) {
    if (delta.kind === "ticket_state_changed" && typeof delta.ticket === "string") {
      ticketStatus.set(delta.ticket, String(delta.status));
    }
  }
  const recoveries = recoveryStatusItems(recoveryLedgerFromRecords(records), ticketStatus);
  return {
    ...(recoveries.length > 0 ? { recoveries } : {}),
    orchestrator_recovery_enabled: isOrchestratorRecoveryActive(config),
  };
}
